import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, CalendarDays, ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";
import { Badge, Btn, Avatar } from "@/components/ui-kit";
import api, { docFileUrl } from "@/lib/api";
import { ScoreBadge, StarRating } from "@/components/PerfScore";
import CertifiedBadge from "@/components/CertifiedBadge";
import ClinicianStatusControls, { ClinicianStatusBadges, ShiftWarning, isAvailable } from "@/pages/ops/ClinicianStatus";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const TH = [
  ["Clinician", "name"], ["Verification"], ["Status"], ["Cases", "cases"], ["Sessions", "sessions"],
  ["Score", "score"], ["Patient rating", "rating"], ["Payout pending", "payout"], [""],
];
const SORTS = TH.filter(([, k]) => k);
const SORT_VAL = {
  name: (c) => (c.name || "").toLowerCase(),
  cases: (c) => c.perf.active_cases,
  sessions: (c) => c.perf.adherence,
  score: (c) => c.perf.perf_score,
  rating: (c) => c.perf.patient_rating,
  payout: (c) => c.perf.payout_pending,
};

function sortList(list, key, dir) {
  if (!key) return list;
  const get = SORT_VAL[key];
  return [...list].sort((a, b) => {
    const va = get(a), vb = get(b);
    if (va == null && vb == null) return 0;
    if (va == null) return 1;
    if (vb == null) return -1;
    const r = typeof va === "string" ? va.localeCompare(vb) : va - vb;
    return dir === "asc" ? r : -r;
  });
}

function VerifyCell({ c, onChange }) {
  if (c.credential_status === "Verified") return <Badge tone="emerald">Verified</Badge>;
  const missing = c.missing_docs || [];
  const verify = async () => {
    try { await api.put(`/ops/clinicians/${c.id}/verify`, { credential_status: "Verified" }); toast.success(`${c.name} verified`); onChange(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not verify"); }
  };
  return (
    <div className="space-y-1">
      <Badge tone="amber">{c.credential_status || "Pending"}</Badge>
      <Btn size="sm" variant="primary" onClick={verify} disabled={missing.length > 0} className="disabled:opacity-40 disabled:cursor-not-allowed"
        title={missing.length ? `Missing: ${missing.map((m) => (m === "insurance" ? "insurance or waiver" : m)).join(", ")}` : ""}
        data-testid={`ops-list-verify-${c.id}`}><ShieldCheck size={13} /> Verify</Btn>
      {missing.length > 0 && <div className="text-[10.5px] text-amber-600 max-w-[150px]" data-testid={`ops-list-missing-${c.id}`}>Missing {missing.length} doc{missing.length === 1 ? "" : "s"}</div>}
    </div>
  );
}

function Row({ c, onOpen, onChange }) {
  return (
    <tr onClick={() => onOpen(c.id)} title="Open clinician details"
      className={`border-b border-slate-100 last:border-0 align-top cursor-pointer hover:bg-teal-50/40 transition-colors ${isAvailable(c) ? "" : "bg-slate-50/80"}`} data-testid={`ops-clinician-row-${c.id}`}>
      <td className="px-3 py-3"><Who c={c} /></td>
      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}><VerifyCell c={c} onChange={onChange} /></td>
      <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}><StatusCell c={c} onChange={onChange} /></td>
      <td className="px-3 py-3 text-[13px] whitespace-nowrap"><b>{c.perf.active_cases}</b> <span className="text-slate-400">active</span><div className="text-[11px] text-slate-400">{c.perf.completed_cases} completed</div></td>
      <td className="px-3 py-3 text-[13px] whitespace-nowrap">{c.perf.sessions_done}/{c.perf.sessions_total}<div className="text-[11px] text-slate-400">{c.perf.adherence}%</div></td>
      <td className="px-3 py-3 text-[14px] whitespace-nowrap"><ScoreBadge score={c.perf.perf_score} isNew={c.perf.is_new} testid={`ops-list-score-${c.id}`} /></td>
      <td className="px-3 py-3"><StarRating value={c.perf.patient_rating} count={c.perf.patient_rating_count} testid={`ops-list-stars-${c.id}`} /></td>
      <td className="px-3 py-3 text-[13px] whitespace-nowrap">{money(c.perf.payout_pending)}<div className="mt-1"><Badge tone={c.agreement_active ? "emerald" : "slate"}>{c.agreement_active ? "Agreement" : "No agreement"}</Badge></div></td>
      <td className="px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}><RosterBtn c={c} onOpen={onOpen} /></td>
    </tr>
  );
}

function Who({ c }) {
  return (
    <div className="flex items-center gap-2.5 text-left min-w-0" data-testid={`ops-list-open-${c.id}`}>
      <Avatar name={c.name} size={36} src={docFileUrl(c.photo_doc_id)} />
      <span className="min-w-0">
        <span className="flex items-center gap-1 font-semibold text-[13px] text-slate-900 break-words">{c.name}<CertifiedBadge comp={c.compliance} testid={`ops-list-certified-${c.id}`} /></span>
        <span className="block text-[11px] text-slate-500 break-words">{c.specialty || "—"} · {c.city || "—"}</span>
        <span className="block text-[10.5px] text-slate-400">{c.prq_id || "—"}</span>
      </span>
    </div>
  );
}

function StatusCell({ c, onChange }) {
  return (
    <>
      <div className="flex gap-1 mb-1.5 flex-wrap"><ClinicianStatusBadges c={c} /></div>
      <ClinicianStatusControls c={c} onChange={onChange} />
      <div className="mt-1.5"><ShiftWarning c={c} onChange={onChange} /></div>
    </>
  );
}

function RosterBtn({ c, onOpen, testid }) {
  return <Btn size="sm" onClick={() => onOpen(c.id, "roster")} data-testid={testid || `ops-list-roster-${c.id}`}><CalendarDays size={13} /> Roster</Btn>;
}

function Stat({ label, children }) {
  return <div className="min-w-0"><div className="text-[10.5px] uppercase tracking-wide text-slate-400">{label}</div><div className="text-[13px] text-slate-800">{children}</div></div>;
}

function MobileRow({ c, onOpen, onChange }) {
  return (
    <div onClick={() => onOpen(c.id)} className={`p-4 border-b border-slate-100 last:border-0 cursor-pointer ${isAvailable(c) ? "" : "bg-slate-50/80"}`} data-testid={`ops-clinician-mrow-${c.id}`}>
      <div className="flex items-start gap-2"><div className="flex-1 min-w-0"><Who c={c} /></div><div onClick={(e) => e.stopPropagation()}><RosterBtn c={c} onOpen={onOpen} testid={`ops-mlist-roster-${c.id}`} /></div></div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-3">
        <Stat label="Cases"><b>{c.perf.active_cases}</b> active</Stat>
        <Stat label="Sessions">{c.perf.sessions_done}/{c.perf.sessions_total} · {c.perf.adherence}%</Stat>
        <Stat label="Score"><ScoreBadge score={c.perf.perf_score} isNew={c.perf.is_new} /></Stat>
        <Stat label="Payout pending">{money(c.perf.payout_pending)}</Stat>
      </div>
      <div className="mt-2 flex items-center gap-2 flex-wrap"><StarRating value={c.perf.patient_rating} count={c.perf.patient_rating_count} /><Badge tone={c.agreement_active ? "emerald" : "slate"}>{c.agreement_active ? "Agreement" : "No agreement"}</Badge></div>
      <div className="mt-3 pt-3 border-t border-dashed border-slate-200 flex flex-wrap gap-3 items-start" onClick={(e) => e.stopPropagation()}>
        <VerifyCell c={c} onChange={onChange} />
        <div className="min-w-0 flex-1"><StatusCell c={c} onChange={onChange} /></div>
      </div>
    </div>
  );
}

export default function ClinicianList({ list, onOpen, onChange }) {
  const [sort, setSort] = useState({ key: null, dir: "desc" });
  const tap = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "desc" ? "asc" : "desc" } : { key, dir: key === "name" ? "asc" : "desc" }));
  const rows = sortList(list, sort.key, sort.dir);
  return (
    <div className="pq-card" data-testid="ops-clinician-list">
      <div className="xl:hidden">
        <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-100 text-[12px] text-slate-500">
          Sort
          <select className="pq-input py-1 flex-1 min-w-0" value={sort.key || ""} onChange={(e) => setSort({ key: e.target.value || null, dir: e.target.value === "name" ? "asc" : "desc" })} data-testid="ops-mlist-sort">
            <option value="">Default</option>
            {SORTS.map(([h, k]) => <option key={k} value={k}>{h}</option>)}
          </select>
        </div>
        {rows.map((c) => <MobileRow key={c.id} c={c} onOpen={onOpen} onChange={onChange} />)}
      </div>
      <table className="w-full hidden xl:table">
        <thead><tr>{TH.map(([h, key]) => (
          <th key={h || "actions"} className="pq-label px-3 py-2.5 text-left border-b border-slate-100 whitespace-nowrap">
            {key ? (
              <button onClick={() => tap(key)} data-testid={`ops-list-sort-${key}`}
                className={`inline-flex items-center gap-1 uppercase hover:text-[#1A3A6D] transition-colors ${sort.key === key ? "text-[#1A3A6D]" : ""}`}>
                {h}
                {sort.key !== key ? <ArrowUpDown size={11} className="opacity-40" /> : sort.dir === "asc" ? <ArrowUp size={11} /> : <ArrowDown size={11} />}
              </button>
            ) : h}
          </th>
        ))}</tr></thead>
        <tbody>{rows.map((c) => <Row key={c.id} c={c} onOpen={onOpen} onChange={onChange} />)}</tbody>
      </table>
    </div>
  );
}
